<template>
    <div v-if="src" class="corrections-card-player">
        <audio ref="audio" :src="src" preload="metadata" />
        <button
            type="button"
            class="corrections-card-player__play"
            :aria-label="isPlaying ? $t('corrections.player.pause') : $t('corrections.player.play')"
            @click="togglePlayback"
        >
            <svg v-if="isPlaying" width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect width="4" height="16" x="6" y="4" stroke="currentColor" stroke-width="2" />
                <rect width="4" height="16" x="14" y="4" stroke="currentColor" stroke-width="2" />
            </svg>
            <svg v-else width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
                <polygon
                    points="5 3 19 12 5 21 5 3"
                    fill="none"
                    stroke="currentColor"
                    stroke-linejoin="round"
                    stroke-width="2"
                />
            </svg>
        </button>
        <input
            class="corrections-card-player__progress"
            type="range"
            min="0"
            :max="total || 0"
            :value="elapsed"
            :style="{ '--progress': `${progress}%` }"
            :aria-label="$t('corrections.player.progress')"
            @input="seek"
        />
        <span class="corrections-card-player__time">{{ timeLabel }}</span>
        <button type="button" class="corrections-card-player__speed" @click="cycleSpeed">
            {{ speed }}x
        </button>
        <button
            type="button"
            class="corrections-card-player__volume"
            :aria-label="isMuted ? $t('corrections.player.unmute') : $t('corrections.player.mute')"
            @click="toggleMute"
        >
            <svg
                v-if="isMuted"
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
            >
                <path d="M11 5 6 9H2v6h4l5 4z" />
                <path d="m22 9-6 6" />
                <path d="m16 9 6 6" />
            </svg>
            <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M11 5 6 9H2v6h4l5 4z" />
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
            </svg>
        </button>
    </div>
</template>

<script>
const SPEEDS = [1, 1.25, 1.5, 2];

const formatTime = (value) => {
    const seconds = Math.floor(Number(value) || 0);
    const minutes = Math.floor(seconds / 60);
    return `${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
};

export default {
    name: 'CorrectionsCardPlayer',
    props: {
        src: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            audio: undefined,
            elapsed: 0,
            total: 0,
            isPlaying: false,
            isMuted: false,
            speed: 1,
        };
    },
    computed: {
        timeLabel() {
            return `${formatTime(this.elapsed)} / ${formatTime(this.total)}`;
        },
        progress() {
            if (!this.total) return 0;
            return Math.min(100, (this.elapsed / this.total) * 100);
        },
    },
    mounted() {
        this.audio = this.$refs.audio;
        if (!this.audio) return;
        this.audio.addEventListener('loadedmetadata', this.onLoadedMetadata);
        this.audio.addEventListener('timeupdate', this.onTimeUpdate);
        this.audio.addEventListener('ended', this.onEnded);
    },
    beforeUnmount() {
        if (!this.audio) return;
        this.audio.removeEventListener('loadedmetadata', this.onLoadedMetadata);
        this.audio.removeEventListener('timeupdate', this.onTimeUpdate);
        this.audio.removeEventListener('ended', this.onEnded);
    },
    methods: {
        async togglePlayback() {
            if (this.isPlaying) {
                this.audio.pause();
                this.isPlaying = false;
                return;
            }

            try {
                await this.audio.play();
                this.isPlaying = true;
            } catch {
                // Playback was blocked (e.g. browser autoplay policy) or
                // interrupted; keep the button in the "paused" state
                // instead of leaving an unhandled rejection.
                this.isPlaying = false;
            }
        },
        onLoadedMetadata(event) {
            this.total = event.target.duration || 0;
        },
        onTimeUpdate(event) {
            this.elapsed = event.target.currentTime || 0;
            this.total = event.target.duration || this.total;
        },
        onEnded() {
            this.isPlaying = false;
            this.elapsed = 0;
        },
        seek(event) {
            const position = Number(event.target.value);
            this.audio.currentTime = position;
            this.elapsed = position;
        },
        cycleSpeed() {
            const nextIndex = (SPEEDS.indexOf(this.speed) + 1) % SPEEDS.length;
            this.speed = SPEEDS[nextIndex];
            this.audio.playbackRate = this.speed;
        },
        toggleMute() {
            this.isMuted = !this.isMuted;
            this.audio.muted = this.isMuted;
        },
    },
};
</script>

<style src="./CorrectionsCardPlayer.scss" lang="scss"></style>
